import {UyapHacizEvrakTuru} from "@/plugins/uyap-plugin/enum/uyap/evrak/UyapHacizEvrakTuru";
import {TaskTuru} from "@/plugins/uyap-plugin/enum/TaskTuru";
import {Task} from "@/plugins/uyap-plugin/store/modules/GorevYoneticisiTypes";
import {IAvukatDosya} from "@/plugins/uyap-plugin/uyap/CommonInterfaces";
import {SorguOneriEntity, SorguOneriRow} from "@/services/sorguOneriService";

/**
 * Öneri kartı ile mevcut "Toplu Haciz Hazırla" sihirbazı arasındaki ince adaptör
 * (F2-İP6, DEV-2953).
 *
 * Sihirbazın kendisi DEĞİŞTİRİLMEZ; buradan üretilen değerler `TopluHacizHazirlaView`
 * tarafından sihirbazın kendi genel arayüzüne (`value` prop'u + halka açık alanları)
 * yazılır.
 *
 * Bu modül saf fonksiyonlardan oluşur: HTTP, Vue ve UYAP bağımlılığı yoktur, birim
 * testi doğrudan yazılabilir.
 */

/** `dosya_ref = "<birimId>:<dosyaEsasNo>"` ayrıştırma sonucu. */
export interface DosyaKimligi {
    birimId: string;
    dosyaNo: string;
}

/** Sihirbaza yazılacak hazır girdi. */
export interface KopruGirdisi {
    /** Sihirbazın 2. adım tablosuna konacak tek satır. */
    takip: IAvukatDosya;
    /** `mdb.hacizEvrakTipleri` içinden seçilecek talep türünün kodu. */
    talepKodu: number;
    /** Kullanıcıya "hangi varlık için" diye gösterilecek etiket (maskeli olabilir). */
    entityEtiketi: string;
    /** Öneriyi doğuran snapshot alanı (`egm`, `takbis`, `bankahesap`, ...). */
    alan: string | null;
    /**
     * Öneriye konu varlığın snapshot gövdesi (`{key, label, attrs}`).
     *
     * Etiketten AYRI taşınır: "Talepleri Hazırla" sonrası sihirbazın varlık
     * listesinde seçimi bu varlığa daraltmak için `key` ve `attrs` gerekiyor
     * (bkz. `aracSecimiDaralt`, `tasinmazSecimiDaralt`).
     */
    entity: SorguOneriEntity | null;
}

/**
 * Kural adı → UYAP talep türü kodu.
 *
 * `tebligat-yenile` BİLEREK yok: Ö2 kararı gereği o kural v1'de köprüsüz bilgi
 * kartıdır (mernis snapshot'ı yalnız {il, ilce, mahalle, acikAdres} taşıyor;
 * yedi tebligat evrakından hangisinin gönderileceği snapshot'tan türetilemiyor).
 *
 * `banka-haczi` kuralı "Banka Haczi Talebi"ne (talepKodu 4) eşlenir — ürün kararı
 * (DEV-2950 yorumları): yeni hesap bulununca kullanıcı borçlunun banka hesabına
 * haciz koymak ister.
 *
 * `banka-89-1` AYRI bir kuraldır ve "89-1 İhbarname Talebi"ne (talepKodu 5) eşlenir.
 * Faz 2'de 89/1 bilerek kapsam dışıydı ("ayrı bir iş akışı") ; avukat teyidiyle
 * (bkz. docs/sorgu-snapshot-gecmis.md, 21 Ağustos karar turu: 89/1) backend artık yeni banka hesabında banka haczine EK
 * OLARAK 89/1 önerisi de üretiyor. İki kural birbirinden bağımsız, iki ayrı talebe
 * gidiyor; biri diğerinin yerine geçmiyor.
 *
 * `posta-ceki-haczi` — `banka-haczi`nin simetriği, "Posta Çeki Haczi Talebi"ne
 * (talepKodu 3) eşlenir. Parametresiz "seç ve gönder" talebi olduğu için (banka-haczi
 * gibi) sihirbazda ayrı bir form taşımaz; köprünün varlık daraltması (araç/taşınmaz,
 * bkz. `hedefVarlikSecimiDaralt`) posta çekinde de UYGULANMAZ — o fonksiyon yalnız
 * `alan === "egm"` / `"takbis"` dallarını tanır, `postaceki` her iki dala da girmez.
 */
export const KURAL_TALEP_KODU: Record<string, number> = {
    "arac-haczi": UyapHacizEvrakTuru.egm,
    "tapu-haczi": UyapHacizEvrakTuru.takbis,
    "maas-haczi": UyapHacizEvrakTuru.maas_haczi,
    "banka-haczi": UyapHacizEvrakTuru.banka_haczi,
    "banka-89-1": UyapHacizEvrakTuru.ihbarname89_1,
    "dosyadan-alacak": UyapHacizEvrakTuru.alacakli_oldugu_dosya,
    "posta-ceki-haczi": UyapHacizEvrakTuru.posta_ceki_haciz
};

/**
 * `"1000963:2023/27440"` → `{birimId: "1000963", dosyaNo: "2023/27440"}`.
 *
 * Esas no'nun kendisi `/` içerir ama `:` içermez; bu yüzden yalnız İLK iki nokta
 * ayraç sayılır. Bozuk/eksik değerde null döner — çağıran taraf köprüyü kapatır.
 */
export function dosyaRefCoz(dosyaRef: string | null | undefined): DosyaKimligi | null {
    if (typeof dosyaRef !== "string") return null;

    const ayracIndex = dosyaRef.indexOf(":");
    if (ayracIndex <= 0) return null;

    const birimId = dosyaRef.slice(0, ayracIndex).trim();
    const dosyaNo = dosyaRef.slice(ayracIndex + 1).trim();

    if (birimId === "" || dosyaNo === "") return null;

    return {birimId, dosyaNo};
}

/** Kuralın sihirbaz karşılığı yoksa null döner (ör. `tebligat-yenile`). */
export function kuralTalepKodu(kural: string | null | undefined): number | null {
    if (typeof kural !== "string") return null;
    const kod = KURAL_TALEP_KODU[kural];
    return typeof kod === "number" ? kod : null;
}

/**
 * Kartta "Talep hazırla" düğmesi gösterilsin mi?
 *
 * DİKKAT: icrapro takip eşleşmesi (takip_id) burada ARANMAZ — öneri tablosunda
 * böyle bir sütun zaten yok ve Toplu Haciz sihirbazı da icrapro takip kimliğini
 * hiç kullanmaz (`DosyaBul.dosyaEslestir` kararlı kimlik olarak birimId + dosyaNo
 * kullanır). UYAP dosyası bir icrapro takibine bağlanmamış olsa da köprü çalışır.
 */
export function koprulenebilirMi(oneri: SorguOneriRow | null | undefined): boolean {
    if (!oneri) return false;
    if (kuralTalepKodu(oneri.kural) === null) return false;
    return dosyaRefCoz(oneri.sihirbaz_param?.dosya_ref ?? oneri.dosya_ref) !== null;
}

/**
 * Kullanıcıya gösterilecek varlık etiketi. Kaynak snapshot entity'sidir (maskeli
 * / allowlist veri); ham UYAP cevabına dönülmez.
 */
export function entityEtiketi(oneri: SorguOneriRow): string {
    const entity = oneri.sihirbaz_param?.entity;
    if (entity) {
        if (typeof entity.label === "string" && entity.label !== "") return entity.label;
        if (typeof entity.key === "string" && entity.key !== "") return entity.key;
    }
    return oneri.entity_key ?? "";
}

/**
 * Sihirbazın 2. adım tablosuna konacak satır.
 *
 * `index`/`rowOrder` şart: tablo `item-key="index"` ile çalışır ve `rowOrder`'a
 * göre sıralar; ikisi de yoksa satır seçili görünmez.
 *
 * `task` iskeleti BURADA kurulur, sihirbazın kancasına bırakılmaz. Kanca
 * (`TopluHacizHazirlaFormWizard.onSecilenTakiplerChange`) `task` yokken
 * `$uyapHelper.formatDate(secilenTakip.dosyaAcilisTarihi)` çağırıyor; köprüyle
 * gelen satırda açılış tarihi bilinmediği için o çağrı TypeError atıp sessizce
 * yutuluyordu. `task` dolu gelince kanca satırı hiç ellemiyor — ne istisna
 * kalıyor ne de eksik atama. Kancanın yazdığı alanların aynısı burada yazılır.
 */
export function takipSatiriUret(kimlik: DosyaKimligi, birimAdi: string = ""): IAvukatDosya {
    const task = new Task();
    task.dosya_esas_no = kimlik.dosyaNo;
    task.birim_adi = birimAdi;
    // DosyaBul.dosyaEslestir'in kararlı ölçütü: birim_id + dosya_esas_no.
    task.birim_id = kimlik.birimId;
    task.task_type_id = TaskTuru.toplu_haciz_hazirla;

    return {
        dosyaId: "",
        dosyaNo: kimlik.dosyaNo,
        dosyaDurumKod: 0,
        dosyaDurum: "",
        task: task,
        dosyaTurKod: 0,
        dosyaTur: "",
        // Tarih bilinmiyor: `dosya_ref` yalnız birimId + esas no taşır. Tablo filtresi
        // tarihsiz satırı elemez, "tarihi bilinmiyor" sayar (genelIslemTakipFiltre).
        dosyaAcilisTarihi: undefined as any,
        birimAdi: birimAdi,
        birimId: kimlik.birimId,
        birimTuru1: "",
        birimTuru2: "",
        birimTuru3: "",
        isDavaDosyasiAcilmisMi: false,
        isNew: false,
        // Tablonun item-key'i ve varsayılan sıralama anahtarı.
        index: 0,
        rowOrder: 0
    } as unknown as IAvukatDosya;
}

/**
 * Öneriden sihirbaz girdisini üretir. Köprülenemeyen öneride null döner.
 *
 * `birimAdi` çağıran tarafça (UYAP'ın önbellekli birim listesinden) çözülüp
 * geçilir; çözülemezse boş kalır — eşleştirme zaten birimId üzerinden yapılıyor.
 */
export function kopruGirdisiUret(
    oneri: SorguOneriRow | null | undefined,
    birimAdi: string = ""
): KopruGirdisi | null {
    if (!oneri) return null;

    const talepKodu = kuralTalepKodu(oneri.kural);
    if (talepKodu === null) return null;

    const kimlik = dosyaRefCoz(oneri.sihirbaz_param?.dosya_ref ?? oneri.dosya_ref);
    if (kimlik === null) return null;

    return {
        takip: takipSatiriUret(kimlik, birimAdi),
        talepKodu,
        entityEtiketi: entityEtiketi(oneri),
        alan: oneri.sihirbaz_param?.alan ?? null,
        entity: oneri.sihirbaz_param?.entity ?? null
    };
}

/** Önerinin hedeflediği borçlunun UYAP kişi/kurum kimliği; yoksa null. */
export function borcluKisiKurumId(oneri: SorguOneriRow | null | undefined): number | null {
    const deger = oneri?.sihirbaz_param?.kisiKurumId;
    return typeof deger === "number" && !isNaN(deger) ? deger : null;
}

/**
 * Köprüyle gelindiğinde sihirbazın açılacağı adım.
 *
 * `TopluHacizHazirlaFormWizard.stepperHeaders` sırası:
 *   1 Listelenecek Takipler · 2 Takipleri Seç · 3 Haciz Evraklarını Hazırla
 *   4 Haciz Evraklarını Gönder · 5 İşlemler Sıraya Alındı
 *
 * Dosya ve talep türü zaten önden seçildiği için 1 ve 2 boş tıklamadır; kullanıcı
 * doğrudan 3'te başlar ve "Talepleri Hazırla"ya kendisi basar.
 */
export const KOPRU_BASLANGIC_ADIMI = 3;

/** `sihirbazaUygula` neyi yazabildi — çağıran taraf eksik kalırsa yeniden dener. */
export interface SihirbazYazmaSonucu {
    talepYazildi: boolean;
    takipYazildi: boolean;
    adimAyarlandi: boolean;
    /** Atlanan `nextStep()` çağrılarının yan etkileri üretildi mi (bkz. adimYanEtkileri). */
    yanEtkilerUretildi: boolean;
    tamam: boolean;
}

/**
 * `nextStep()` atlandığında KAYBOLAN yan etkileri elle üretir.
 *
 * `GenelIslemFormWizard.nextStep()` adım artışının yanında iki şey daha yapıyor:
 * `$emit('step', activeStep)` ve `focusStep(activeStep)`. Köprü `activeStep`'i
 * doğrudan atadığı için ikisi de çalışmaz. Emit önemlidir: dış sihirbaz onu
 * `@step="onStepChange"` ile dinliyor ve 3. adımda `hideNext = true` yapıyor —
 * üretilmezse 3. adımda "Talepleri Hazırla" ile genel "Devam" yan yana kalır,
 * kullanıcı "Devam"a basarsa hiçbir görev hazırlanmadan 4. adıma geçer ve
 * gönderim "Talep gönderilecek borçlu seçiniz" uyarısıyla boşa düşer.
 *
 * Emit gerçek kanaldır (sihirbaz ileride `onStepChange`'e başka iş eklerse o da
 * çalışır); `hideNext` doğrudan yazımı yalnız emit'in ulaşmadığı hâller için
 * güvenlik ağıdır. `focusStep` salt kozmetiktir (stepper başlığını yatay
 * kaydırır), `$refs` hazır değilse patlayabildiği için korumalı çağrılır.
 *
 * DİKKAT: 1. adımın yan etkisi (`getTakipler()` → UYAP listeleme isteği + seçim
 * sıfırlama) BİLEREK üretilmez; köprü zaten onu atlamak için var ve bu fonksiyon
 * hiçbir UYAP isteği tetiklemez.
 */
export function adimYanEtkileri(sihirbaz: any, adim: number): boolean {
    if (!sihirbaz) return false;

    const icForm = sihirbaz.genelIslemForm;
    let uretildi = false;

    if (icForm && typeof icForm.$emit === "function") {
        icForm.$emit("step", adim);
        uretildi = true;
    }

    // Emit ulaşmadıysa (ya da dinleyici yoksa) 3. adımın tek gerçek etkisini
    // doğrudan yaz: genel "Devam" düğmesi gizlensin.
    if (adim === KOPRU_BASLANGIC_ADIMI && sihirbaz.hideNext !== true) {
        sihirbaz.hideNext = true;
        uretildi = true;
    }

    if (icForm && typeof icForm.focusStep === "function") {
        try {
            icForm.focusStep(adim);
        } catch (e) {
            // Salt kozmetik; stepper basligi kaydirilamazsa akis etkilenmez.
        }
    }

    return uretildi;
}

/**
 * Ön dolu girdiyi çalışan sihirbaza yazar. Sihirbaz DOSYALARI değişmez; yalnız
 * halka açık alanlarına yazılır.
 *
 * Neden iki katmana birden yazılıyor: `TopluHacizHazirlaFormWizard` dıştaki
 * sarmalayıcıdır ve `SimpleInputMixin` sayesinde `value` prop'unu (immediate
 * watcher) alır; ama içteki `GenelIslemFormWizard` KENDİ `localValue`'sunu
 * `new GenelIslemEntity()` ile başlatıp `ObjectInputMixin`'in immediate OLMAYAN
 * `@Watch('value')`'una güvenir. `value` oluşumdan sonra hiç değişmediği için o
 * izleyici hiç tetiklenmez ve iç katman BOŞ kalır — 2. adım tablosu boş açılır,
 * "Devam" da `secilen_takipler.length == 0` diye kilitlenir. Bu yüzden satır
 * doğrudan iç katmanın `localValue`'suna yazılır.
 *
 * Adımın doğrudan atanmasının nedeni ayrı: 1. adımın "Devam" düğmesi
 * `getTakipler()` çağırıyor, o da `takipler`/`secilen_takipler`'i sıfırlıyor.
 * Doğrudan atama `nextStep()`'i hiç çalıştırmadığı için HİÇBİR UYAP isteği
 * tetiklenmez; atlanan geçişlerin yan etkileri `adimYanEtkileri` ile üretilir.
 *
 * Vue bağımlılığı yok: `sihirbaz` ördek-tipli okunur, böylece birim testi
 * bileşen mount etmeden yazılabilir.
 */
export function sihirbazaUygula(
    sihirbaz: any,
    girdi: KopruGirdisi | null | undefined,
    talep: any = null
): SihirbazYazmaSonucu {
    const sonuc: SihirbazYazmaSonucu = {
        talepYazildi: false,
        takipYazildi: false,
        adimAyarlandi: false,
        yanEtkilerUretildi: false,
        tamam: false
    };

    if (!sihirbaz || !girdi) return sonuc;

    if (talep) {
        sihirbaz.talepler = [talep];
        sonuc.talepYazildi = true;
    }

    const icForm = sihirbaz.genelIslemForm;
    if (icForm && icForm.localValue) {
        // Aynı nesne referansı iki listeye de konur: tablo `item-key="index"` ile
        // eşleştirdiği için satır seçili görünür.
        icForm.localValue.takipler = [girdi.takip];
        icForm.localValue.secilen_takipler = [girdi.takip];
        sonuc.takipYazildi = true;

        if (typeof icForm.activeStep === "number") {
            icForm.activeStep = KOPRU_BASLANGIC_ADIMI;
            sonuc.adimAyarlandi = true;
            sonuc.yanEtkilerUretildi = adimYanEtkileri(sihirbaz, KOPRU_BASLANGIC_ADIMI);
        }
    }

    // kod inceleme: üçlü koşuldaki `talep ? sonuc.talepYazildi : true` hiçbir
    // zaman false olamıyordu (talepYazildi düz bir alan ataması, asla
    // başarısız olmaz) — ölü savunma kaldırıldı.
    sonuc.tamam = sonuc.takipYazildi && sonuc.adimAyarlandi;
    return sonuc;
}

/**
 * Köprüyle gelen satır sihirbazda hâlâ seçili mi?
 *
 * 1. adıma dönüp yeniden listeleme yapılırsa sihirbaz `secilen_takipler`'i
 * sıfırlıyor; o an köprü artık geçersizdir (bilgi şeridi yalan söylememeli,
 * öneri de yanlışlıkla "görev oluşturuldu" işaretlenmemeli).
 */
export function kopruHalaGecerliMi(sihirbaz: any, girdi: KopruGirdisi | null | undefined): boolean {
    if (!sihirbaz || !girdi) return false;

    const listeler = [
        sihirbaz.genelIslemForm?.localValue?.secilen_takipler,
        sihirbaz.localValue?.secilen_takipler
    ];

    return listeler.some((liste: any) => Array.isArray(liste) && liste.indexOf(girdi.takip) !== -1);
}

/**
 * "Hazırla" adımı bittikten sonra borçlu seçimini öneriye daraltır (F2-K7).
 *
 * Sihirbaz sorgu biter bitmez dosyadaki TÜM borçluları işaretliyor
 * (`borclu.selected = true`). Köprü tek bir borçlu için açıldığından, eşleşmeyen
 * borçluların seçimi kaldırılır — aksi halde ilgisiz borçluya da haciz evrakı
 * kuyruğa girerdi. Eşleşen borçlu bulunamazsa HİÇBİR ŞEY yapılmaz (kullanıcı
 * mevcut davranışı görsün, sessizce hepsi seçimsiz kalmasın).
 *
 * @param yaz Vue reaktivitesi için `$set` benzeri yazıcı; verilmezse düz atama.
 * @returns Seçimi kaldırılan borçlu sayısı; daraltma yapılmadıysa 0.
 */
export function borcluSecimiDaralt(
    borclular: any[] | null | undefined,
    kisiKurumId: number | null,
    yaz?: SecimYazici
): number {
    if (!Array.isArray(borclular) || borclular.length === 0) return 0;
    if (kisiKurumId === null) return 0;

    const eslesenVar = borclular.some((borclu: any) => Number(borclu?.kisiKurumId) === kisiKurumId);
    if (!eslesenVar) return 0;

    const ata = yaz ?? VARSAYILAN_SECIM_YAZICI;

    let kaldirilan = 0;
    borclular.forEach((borclu: any) => {
        const bizimki = Number(borclu?.kisiKurumId) === kisiKurumId;
        if (!bizimki && borclu?.selected !== false) {
            ata(borclu, "selected", false);
            kaldirilan++;
        }
    });

    return kaldirilan;
}

/** Vue reaktivitesi için `$set` benzeri yazıcı; verilmezse düz atama kullanılır. */
export type SecimYazici = (hedef: any, alan: string, deger: any) => void;

// kod inceleme: `borcluSecimiDaralt` ve `varlikSecimiDaralt` aynı varsayılan
// "düz atama" lambda'sını ayrı ayrı tanımlıyordu — tek sabite indirgendi.
const VARSAYILAN_SECIM_YAZICI: SecimYazici = (hedef, alan, deger) => (hedef[alan] = deger);

/** `varlikSecimiDaralt` sonucu — "eşleşti" ile "eşleşti ama kaldıracak bir şey yoktu" ayrılır. */
export interface SecimDaraltmaSonucu {
    /** Ölçüte uyan öğe sayısı. 1 dışındaki her değer BELİRSİZLİKTİR. */
    adaySayisi: number;
    /** Seçimi kaldırılan öğe sayısı. Tek varlıklı listede eşleşme olsa da 0 olur. */
    kaldirilan: number;
}

/**
 * Bir varlık listesinde seçimi TEK bir öğeye daraltır.
 *
 * `borcluSecimiDaralt`'tan bir adım daha katıdır: eşleşme TAM OLARAK BİR TANE
 * olmalıdır. Sıfır eşleşme (varlık listede yok) ve birden çok eşleşme (maskeli
 * plaka aynı, `tasinmazId` mükerrer) ikisi de BELİRSİZDİR; belirsizlikte hiçbir
 * şey değiştirilmez ve mevcut davranış (hepsi seçili) korunur — yanlış varlığa
 * haciz koymaktansa kullanıcıya seçtirmek yeğdir.
 *
 * @returns Seçimi kaldırılan öğe sayısı; daraltma yapılmadıysa 0.
 */
export function varlikSecimiDaralt(
    liste: any[] | null | undefined,
    eslesirMi: (oge: any) => boolean,
    yaz?: SecimYazici
): SecimDaraltmaSonucu {
    if (!Array.isArray(liste) || liste.length === 0) return {adaySayisi: 0, kaldirilan: 0};

    const adaylar = liste.filter((oge) => {
        try {
            return eslesirMi(oge);
        } catch (e) {
            return false;
        }
    });

    // Tam olarak bir aday: 0 => varlik listede yok, >=2 => hangisi oldugu belirsiz.
    if (adaylar.length !== 1) return {adaySayisi: adaylar.length, kaldirilan: 0};

    const hedef = adaylar[0];
    const ata = yaz ?? VARSAYILAN_SECIM_YAZICI;

    let kaldirilan = 0;
    liste.forEach((oge) => {
        if (oge === hedef) return;
        if (oge?.selected === false) return;
        ata(oge, "selected", false);
        kaldirilan++;
    });

    return {adaySayisi: 1, kaldirilan};
}

/** Karşılaştırma için metin normalizasyonu: yalnız kırpma, biçim bozulmaz. */
function metin(deger: unknown): string {
    if (deger === null || deger === undefined) return "";
    return String(deger).trim();
}

/**
 * `beklenen` snapshot attrs'ından geliyor. Attrs'ta HİÇ YOKSA `null` döner —
 * çağıran taraf bunu "doğrulanamadı" sayar.
 */
function attrEsit(beklenen: unknown, gercek: unknown): boolean | null {
    if (beklenen === undefined || beklenen === null || beklenen === "") return null;
    return metin(beklenen) === metin(gercek);
}

/**
 * Araç seçimini öneriye konu araca daraltır (EGM).
 *
 * `DosyaBorcluEgm.parse()` her araca koşulsuz `selected = true` yazıyor
 * (uyap-plugin, değiştirilemez); köprüyle gelindiğinde dosyadaki tüm araçlara
 * haciz + yakalama gitmesin diye seçim tek araca indirilir.
 *
 * Eşleştirme: canlı `arac.plaka` da snapshot `entity.key` gibi MASKELİDİR
 * ("60***37") ve ikisi de aynı UYAP alanından gelir — düz string eşitliği
 * kullanılır, bulanık eşleştirme YAPILMAZ (maske genişliği kayıttan kayda
 * değişebiliyor: "06***51" / "06****27").
 *
 * Çifte doğrulama: `attrs.marka` ZORUNLUdur (yoksa daraltma yapılmaz), `attrs.model`
 * varsa o da uymalıdır. `hacizSerhi` doğrulamada KULLANILMAZ — parser her koşumda
 * 0'a sıfırlıyor, her zaman eşleşir ve yanlış güven verir.
 */
export function aracSecimiDaralt(
    araclar: any[] | null | undefined,
    entity: SorguOneriEntity | null | undefined,
    yaz?: SecimYazici
): SecimDaraltmaSonucu {
    const hedefPlaka = metin(entity?.key);
    if (hedefPlaka === "") return {adaySayisi: 0, kaldirilan: 0};

    const attrs = entity?.attrs ?? {};
    // Cifte dogrulama sart: marka yoksa yalnizca maskeli plakaya guvenilmez.
    if (metin(attrs.marka) === "") return {adaySayisi: 0, kaldirilan: 0};

    return varlikSecimiDaralt(
        araclar,
        (arac: any) => {
            if (metin(arac?.plaka) !== hedefPlaka) return false;
            if (attrEsit(attrs.marka, arac?.marka) !== true) return false;
            const modelSonucu = attrEsit(attrs.model, arac?.model);
            return modelSonucu !== false;
        },
        yaz
    );
}

/**
 * Taşınmaz seçimini öneriye konu taşınmaza daraltır (TAKBİS).
 *
 * `BorcluTakbisSorgulama.parse()` de her taşınmaza `selected = true` yazıyor.
 * Anahtar `tasinmazDVO.tasinmazId` — maskesiz, düz sayısal kimlik; araçtaki
 * maskeli plakadan daha güvenilir. Yine de TEKİLLİK GARANTİ DEĞİL: aynı
 * `tasinmazId` gerçek UYAP cevabında iki kez geçebiliyor (takbis normalizer'ın
 * kendi notu) — mükerrer kayıtta iki aday çıkar ve daraltma yapılmaz.
 *
 * Çifte doğrulama `attrs.ada` / `attrs.parsel` ile yapılır (varsa).
 */
export function tasinmazSecimiDaralt(
    tasinmazlar: any[] | null | undefined,
    entity: SorguOneriEntity | null | undefined,
    yaz?: SecimYazici
): SecimDaraltmaSonucu {
    const hedefId = metin(entity?.key);
    if (hedefId === "") return {adaySayisi: 0, kaldirilan: 0};

    const attrs = entity?.attrs ?? {};

    return varlikSecimiDaralt(
        tasinmazlar,
        (tasinmaz: any) => {
            if (metin(tasinmaz?.tasinmazDVO?.tasinmazId) !== hedefId) return false;
            if (attrEsit(attrs.ada, tasinmaz?.tasinmazDVO?.ada) === false) return false;
            return attrEsit(attrs.parsel, tasinmaz?.tasinmazDVO?.parsel) !== false;
        },
        yaz
    );
}

/** Bir daraltma turunun sonucu — kullanıcıya ne söyleneceğini belirler. */
export interface VarlikDaraltmaSonucu {
    /** Daraltmanın hedeflediği varlık türü; alan desteklenmiyorsa null. */
    tur: "arac" | "tasinmaz" | null;
    /**
     * `desteklenmiyor` — kural bu daraltmaya uygun değil (banka/maaş).
     * `beklemede`      — liste henüz boş; sorgu yapılmadı ya da sonuç gelmedi.
     * `eslesti`        — tek aday bulundu; seçim öneriye uydu (kaldirilan 0 olabilir:
     *                    dosyada zaten tek varlık varsa kaldıracak bir şey yoktur).
     * `belirsiz`       — liste dolu ama aday sayısı 1 değil; BİLEREK dokunulmadı,
     *                    seçim kullanıcıda kaldı.
     */
    durum: "desteklenmiyor" | "beklemede" | "eslesti" | "belirsiz";
    /** Seçimi kaldırılan öğe sayısı. */
    kaldirilan: number;
}

/** Liste ve aday sayısından kullanıcıya söylenecek durumu türetir. */
function daraltmaDurumu(liste: any, sonuc: SecimDaraltmaSonucu): VarlikDaraltmaSonucu["durum"] {
    if (!Array.isArray(liste) || liste.length === 0) return "beklemede";
    return sonuc.adaySayisi === 1 ? "eslesti" : "belirsiz";
}

/**
 * Öneriyi doğuran alana göre doğru varlık listesini bulup daraltmayı uygular.
 *
 * Yalnız `egm` ve `takbis` desteklenir; ikisinde de seçim, sorgu cevabının KENDİ
 * listesindeki `selected` bayrağıyla yapılıyor ve anahtar snapshot'tan geliyor.
 *
 * `bankahesap` DIŞARIDA: gönderime giden banka seçimi sorgu sonucundaki listeden
 * değil, ayrı bir bileşenin ayrı bir UYAP çağrısıyla doldurduğu farklı şekilli
 * katalogdan (`bankaHesapHacziParams.bankaList`) besleniyor; `eftKodu` orada yok.
 * `sgkssk` (maaş) de dışarıda: işveren seçimi çoklu liste değil tekil bir picker,
 * daraltılacak bir "hepsi seçili" durumu yok.
 */
export function hedefVarlikSecimiDaralt(
    borclu: any,
    girdi: KopruGirdisi | null | undefined,
    yaz?: SecimYazici
): VarlikDaraltmaSonucu {
    if (!borclu || !girdi || !girdi.entity) return {tur: null, durum: "desteklenmiyor", kaldirilan: 0};

    if (girdi.alan === "egm") {
        const araclar = borclu?.egm?.sorguSonucDVO?.aracBilgileri?.aracList;
        const sonuc = aracSecimiDaralt(araclar, girdi.entity, yaz);
        return {tur: "arac", durum: daraltmaDurumu(araclar, sonuc), kaldirilan: sonuc.kaldirilan};
    }

    if (girdi.alan === "takbis") {
        // `sorguSonucDVO` dizi DEGILSE (hata metni string gelebiliyor) liste bos sayilir.
        const ham = borclu?.takbis?.sorguSonucDVO;
        const tasinmazlar = Array.isArray(ham) ? ham : null;
        const sonuc = tasinmazSecimiDaralt(tasinmazlar, girdi.entity, yaz);
        return {tur: "tasinmaz", durum: daraltmaDurumu(tasinmazlar, sonuc), kaldirilan: sonuc.kaldirilan};
    }

    return {tur: null, durum: "desteklenmiyor", kaldirilan: 0};
}
